import { useEffect, useRef, useState } from 'react';

export type HashBinding = 'none' | 'bound' | 'broken';

interface HashStripProps {
  readonly hashPrefix: string;
  /**
   * `bound` once a grant exists for this exact content; `broken` once the content has
   * moved away from what the grant attests to.
   */
  readonly binding: HashBinding;
  readonly label?: string;
}

/**
 * The booking's content hash, as discrete cells, with changed characters marked.
 *
 * "Amending the booking invalidates the verification" is the least intuitive claim in
 * the design and the easiest for an audience to nod along to without believing. So the
 * hash is on screen, and when it changes the characters that changed are called out.
 * The binding breaking is something the room watches rather than something it is told.
 *
 * Only the server-computed prefix is rendered. The browser never recomputes the hash —
 * a client-side hash would be a second, untrusted opinion about the booking's content,
 * and reconciling two opinions is exactly the bug this whole mechanism avoids.
 */
export function HashStrip({ hashPrefix, binding, label = 'content hash' }: HashStripProps) {
  const previous = useRef(hashPrefix);
  const [changed, setChanged] = useState<ReadonlySet<number>>(new Set());

  useEffect(() => {
    if (previous.current === hashPrefix) return;

    const moved = new Set<number>();
    for (let index = 0; index < hashPrefix.length; index += 1) {
      if (previous.current[index] !== hashPrefix[index]) moved.add(index);
    }
    previous.current = hashPrefix;
    setChanged(moved);

    // Held long enough to be seen and narrated, then cleared so a later change reads as
    // a new event rather than as more of the same one.
    const timer = window.setTimeout(() => setChanged(new Set()), 4000);
    return () => window.clearTimeout(timer);
  }, [hashPrefix]);

  const modifier = binding === 'none' ? '' : ` hashstrip--${binding}`;

  return (
    <div className={`hashstrip${modifier}`}>
      <span className="hashstrip__cells" aria-hidden="true">
        {[...hashPrefix].map((character, index) => (
          <span
            // Position is the identity here: cell 3 is cell 3 whatever it now holds.
            key={index}
            className={`hashstrip__cell${changed.has(index) ? ' hashstrip__cell--changed' : ''}`}
          >
            {character}
          </span>
        ))}
      </span>
      <span className="hashstrip__label">
        {label}
        {binding === 'bound' ? ' · verified' : null}
        {binding === 'broken' ? ' · no longer matches' : null}
      </span>
      {/* The cells are decorative to a screen reader; this is the actual content. */}
      <span className="visually-hidden">
        {label} {hashPrefix.split('').join(' ')}
        {changed.size > 0
          ? '. This booking changed, so any completed verification no longer applies.'
          : ''}
      </span>
    </div>
  );
}
